import React, { useEffect, useState, useRef, useCallback } from "react";
import { Form, Row, Col, Button, Upload, message, Space, Progress, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { PlusCircleTwoTone, UploadOutlined, LoadingOutlined, CloseCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

import "../../../public/sass/components/drawer/_positionsContainer.scss";
import { EmptyDataCreate } from "../../EmptyDataCreate";
import { LoadingSpinner } from "../../LoadingSpinner";
import Api from "../../../api";
import ApiConstants from "../../../api/ApiConstants";
import PositionsActionTable from "../../Tables/Positions-Action";
import { Filters } from "../../../components/Drawer/Positions-Action/Filter";

import { CreatePositions } from "../../../components/Drawer/Clients/CreatePositionRequests";
import { EditPosition } from "../../../components/Drawer/Positions/EditPosition";

import { toastNotification } from "../../../utils/toastNotification";
import { ExportToExcel } from "../../ExportToExcel";


export const PositionsContainer = ({
  onClose,
  id,
  data,
  getCompanyById,
  lockEditById,
  lock,
  setStatus,
  statusCheck,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [third_parties, setThirdPartiesCompanies] = useState([]);
  const [isVisibleCreate, setVisibleCreate] = useState(false);
  const [editData, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [role, setRoles] = useState();

  const [modalState, setModal] = useState({ id: "", mode: "", isOpen: false });
  const [importPhase, setImportPhase] = useState("idle"); // idle | uploading | processing
  const [importProgress, setImportProgress] = useState(null); // { total, current, succeeded, failed, errors }
  const [importJobId, setImportJobId] = useState(null);
  const pollRef = useRef(null);

  const handleCancel = useCallback(async () => {
    if (!importJobId) return;
    try {
      await Api("import/jobs/" + importJobId + "/cancel", "POST");
    } catch (e) {
      console.error("[Import] Cancel error:", e);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    localStorage.removeItem("activeImportJobId_action_" + data?.id);
    setImportPhase("idle");
    setImportProgress(null);
    setImportJobId(null);
    getProductsByActionId();
    toastNotification("info", t("import_cancelled"));
  }, [importJobId, data?.id, t]);

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await Api("import/jobs/" + jobId, "GET");
        setImportProgress({
          total: statusRes.total_rows,
          current: statusRes.done_rows + statusRes.failed_rows,
          succeeded: statusRes.done_rows,
          failed: statusRes.failed_rows,
          errors: statusRes.errors || [],
        });
        getProductsByActionId();
        if (statusRes.status === "completed" || statusRes.status === "failed" || statusRes.status === "cancelled") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          localStorage.removeItem("activeImportJobId_action_" + data?.id);
          setImportPhase("idle");
          setImportProgress(null);
          setImportJobId(null);
          getProductsByActionId();
          if (statusRes.status !== "cancelled") {
            toastNotification(
              "success",
              `${t("import_completed")} (${statusRes.done_rows} ${t("succeeded")}, ${statusRes.failed_rows} ${t("failed")})`
            );
          }
        }
      } catch (pollErr) {
        console.error("[Import] Poll error:", pollErr);
      }
    }, 2000);
  }, [data?.id, t]);

  useEffect(() => {
    const stored = localStorage.getItem("activeImportJobId_action_" + data?.id);
    if (stored) {
      Api("import/jobs/" + stored, "GET")
        .then((statusRes) => {
          if (statusRes.status === "pending" || statusRes.status === "processing") {
            setImportJobId(stored);
            setImportPhase("processing");
            setImportProgress({
              total: statusRes.total_rows,
              current: statusRes.done_rows + statusRes.failed_rows,
              succeeded: statusRes.done_rows,
              failed: statusRes.failed_rows,
              errors: statusRes.errors || [],
            });
            startPolling(stored);
          } else {
            localStorage.removeItem("activeImportJobId_action_" + data?.id);
          }
        })
        .catch(() => {
          localStorage.removeItem("activeImportJobId_action_" + data?.id);
        });
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [data?.id, startPolling]);

  const toggleDrawer = () => setVisibleCreate(!isVisibleCreate);

  const dataFormatExcel = [
    {
      Positionsname: "",
      Positionsnummer: "",
      Beschreibung: "",
      Kurzbeschreibung: "",
      Verpackungseinheit: "",
      "Menge je VE": "",
      Laufleistung: "",
      "Typ der Laufleistung": "",
      "Art des Gutachtens": "",
      "Zerschlagungswert menge": "",
      Zwischensumme: "",
      "MwSt. - Art": "",
      "MwSt.": "",
      "Gesamtwert inkl. MwSt.": "",
      "Fortführungswert menge": "",
      Zwischensumme: "",
      "MwSt. - Art\tMwSt.": "",
      "Gesamtwert inkl. MwSt": "",
      "Marktwert menge": "",
      Zwischensumme: "",
      "MwSt. - Art": "",
      MwSt: "",
      "Gesamtwert inkl. MwSt.": "",
      Vermögensart: "",
      "Aus- und Absonderungsrechte": "",
      Aussonderungsrechte: "",
      Absonderungsrechte: "",
      "Platz des Artikels": "",
    },
  ];

  const handleSubmit = () => {
    onClose();
  };

  useEffect(() => {
    setStatus(false);
    products.map((item) => {
      if (item.status !== 0) {
        setStatus(true);
      }
    });
  }, [statusCheck, products, third_parties, companies, editData]);

  useEffect(() => {
    getAllCompanies();
    handleThirdPartiesCompanies();
    handleGetAllCategories();
    setLoadingPositions(false);
    handleGetAllRoles();
  }, []);

  useEffect(() => {
    setProducts(data?.products || []);
  }, [data?.products]);

  const handleGetAllCategories = () => {
    Api("categories", "GET", null)
      .then((data) => {
        setCategories(data.results);
      })
      .catch(() => setLoadingCategories(false));
  };

  const getAllCompanies = () => {
    Api("actions", "GET").then((data) => {
      setCompanies(data.results);
    });
  };

  const handleThirdPartiesCompanies = () => {
    Api("third-party", "GET", null).then((data) => {
      setThirdPartiesCompanies(data.results);
    });
  };

  const handleModal = (mode, isOpen, id) => {
    setModal({
      ...modalState,
      ["mode"]: mode,
      ["isOpen"]: isOpen,
      ["id"]: id,
    });
  };

  const getProductById = (id) => {
    setLoading(true);
    Api("products/" + id, "GET", null)
      .then((data) => {
        setData(data);
        handleModal("edit", true, id);
        setLoading(false);
      })
      .catch((err) => {
        toastNotification("error", err.error || `${t("something_went_wrong")}`);
        setLoading(false);
      });
  };

  const deleteProduct = (id) => {
    if (!id) return message.error("Id is missing!");
    Api("products/removeProductFromAction/" + id, "GET", null)
      .then(() => {
        getProductsByActionId();
        toastNotification("success", `${t("product_successfully_deleted")}`);
      })
      .catch(() => toastNotification("error", `${t("error_delete_product")}`));
  };

  const handleGetAllRoles = () => {
    Api("users/roleId", "GET", null)
      .then((data) => {
        setRoles(data.role.id);
      })
      .catch((err) => toastNotification("error", err.error || "Error"));
  };

  const getProductsByActionId = () => {
    Api("products/getProductsByActionId/" + data?.id, "GET", null).then(
      (data) => {
        setProducts(data.results);
      }
    );
  };

  const handleGetAllProducts = () => {
    Api("actions/" + data?.id, "GET", null)
      .then((data) => {
        setProducts(data.products);
      })
      .catch(() => setLoading(false));
  };

  const handleUploadExcel = async (file) => {
    const company_id = localStorage.getItem("company_id").replace(/"/g, "");
    setImportPhase("uploading");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", company_id);
    formData.append("action_id", data?.id);

    try {
      const uploadRes = await fetch(ApiConstants.BASE_URL + "import/upload", {
        method: "POST",
        headers: {
          Authorization: "Bearer " + JSON.parse(localStorage.getItem("auc-token")),
        },
        body: formData,
      });
      const result = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(result.message || "Upload failed");

      const jobId = result.import_job_id;
      localStorage.setItem("activeImportJobId_action_" + data?.id, jobId);
      setImportJobId(jobId);
      setImportPhase("processing");
      setImportProgress({ total: result.total_rows, current: 0, succeeded: 0, failed: 0, errors: [] });
      toastNotification("info", `${t("import_started")} (${result.total_rows} ${t("rows")})`);
      startPolling(jobId);
    } catch (err) {
      console.error("[Import] Fatal error:", err);
      setImportPhase("idle");
      setImportProgress(null);
      setImportJobId(null);
      toastNotification("error", `Import failed: ${err.message}`);
    }
  };

  const csvData = [
    [
      "Positionsname",
      "Positionsnummer",
      "Beschreibung",
      "Kurzbeschreibung",
      "Verpackungseinheit",
      "Menge je VE",
      "Laufleistung",
      "Typ der Laufleistung",
      "Art des Gutachtens",
      "Zerschlagungswert menge",
      "Zwischensumme",
      "MwSt. - Art",
      "MwSt.",
      "Gesamtwert inkl. MwSt.",
      "Fortführungswert menge",
      "Zwischensumme",
      "MwSt. - Art	MwSt.",
      "Gesamtwert inkl. MwSt",
      "Marktwert menge",
      "Zwischensumme",
      "MwSt. - Art",
      "MwSt",
      "Gesamtwert inkl. MwSt.",
      "Vermögensart",
      "Aus- und Absonderungsrechte",
      "Aussonderungsrechte",
      "Absonderungsrechte",
      "Platz des Artikels",
    ],
  ];

  const renderImportControls = () => (
    <>
      {importPhase === "idle" && (
        <Upload
          accept=".xlsm, .xltm, .xlt, .xls, .xlsx, .xltx, .xlsb, .xml, .xlam, .xla, .xlw, .xlr"
          beforeUpload={(file) => {
            handleUploadExcel(file);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />}>{t("upload_excel")}</Button>
        </Upload>
      )}
      {importPhase === "uploading" && (
        <div className="import-progress-inline">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          <span>{t("uploading")}...</span>
        </div>
      )}
      {importPhase === "processing" && (
        <div className="import-progress-inline">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          <span>
            {importProgress?.current || 0}/{importProgress?.total || 0} {t("positions")}
          </span>
          <Progress
            percent={importProgress?.total ? Math.round((importProgress.current / importProgress.total) * 100) : 0}
            size="small"
            style={{ width: 80, margin: 0 }}
          />
          <Button type="text" danger size="small" icon={<CloseCircleOutlined />} onClick={handleCancel} />
        </div>
      )}
    </>
  );

  const renderImportExport = () => (
    <div className="flex-buttons">
      {renderImportControls()}
      <ExportToExcel t={t} apiData={dataFormatExcel} fileName="PositionenImport" />
    </div>
  );

  const renderActionRow = (btnLabel) => (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item name="more_products">
          <Button className="button-style" onClick={toggleDrawer}>
            <PlusCircleTwoTone className="icon-style" /> {btnLabel}
          </Button>
        </Form.Item>
      </Col>
      <Col span={5}></Col>
      <Col span={7}>{renderImportExport()}</Col>
    </Row>
  );

  if (loadingPositions) return <LoadingSpinner />;
  if (!products?.length)
    return (
      <>
        {renderActionRow(t("add_position"))}
        <EmptyDataCreate
          btnTitle={t("add_position")}
          message={t("no_positions_found")}
          handleOnClick={toggleDrawer}
        />
        <CreatePositions
          id={id}
          isVisible={isVisibleCreate}
          onClose={toggleDrawer}
          handleGetAllProducts={getProductsByActionId}
          getCompanyById={getCompanyById}
          companies={companies}
          categories={categories}
          third_parties={third_parties}
          products={products}
          action_id={id}
          role={role}
        />
      </>
    );

  return (
    <Form
      form={form}
      id="customers-form"
      layout="horizontal"
      scrollToFirstError
      onFinish={handleSubmit}
      className="form-style"
    >
      {renderActionRow(t("more_products"))}
      <Filters />
        <PositionsActionTable
          products={products}
          setProducts={setProducts}
          getProductById={getProductById}
          deleteProduct={deleteProduct}
          // loading={loading}
          companies={companies}
          lockEditById={lockEditById}
          lock={lock}
          id={id}
        />
      <EditPosition
        editData={editData}
        id={modalState.id}
        isVisible={modalState.mode == "edit" && modalState.isOpen}
        handleGetAllProducts={handleGetAllProducts}
        onClose={handleModal}
        companies={companies}
        third_parties={third_parties}
        categories={categories}
        products={products}
      />
      <CreatePositions
        id={id}
        data={data}
        isVisible={isVisibleCreate}
        onClose={toggleDrawer}
        handleGetAllProducts={getProductsByActionId}
        getCompanyById={getCompanyById}
        companies={companies}
        categories={categories}
        third_parties={third_parties}
        products={products}
        action_id={id}
        role={role}
      />
    </Form>
  );
};
